import type { CageHealthParameter } from '@/domain/cage-health-overview';
import type { HealthParameterKey } from '@/domain/health';

/** Compact cage dashboard card shows at most four Laksvel parameters. */
export const CAGE_DASHBOARD_HEALTH_PARAMETER_LIMIT = 4;

/**
 * Fill order after alerts: wounds → maturation → scale loss → deformities → …
 */
export const CAGE_DASHBOARD_HEALTH_PARAMETER_FILL_ORDER: readonly HealthParameterKey[] = [
    'bodyWounds',
    'snoutWounds',
    'maturity',
    'scaleLoss',
    'deformity',
    'deformedJaw',
    'finDamage',
    'emaciation',
    'cataract',
    'skinBleeding',
    'eyeDamage',
    'gillLid',
];

export function isCageHealthAlertParameter(parameter: Pick<CageHealthParameter, 'alert'>): boolean {
    return parameter.alert.level !== 'stable';
}

function compareByAlertPriority(a: CageHealthParameter, b: CageHealthParameter): number {
    return b.alert.priority - a.alert.priority;
}

/**
 * Full list for the cage card: alerts first (by severity), then fill order.
 * Expanding keeps the first four in place.
 */
export function orderCageHealthOverviewParameters(
    parameters: CageHealthParameter[]
): CageHealthParameter[] {
    const byKey = new Map(parameters.map((parameter) => [parameter.key, parameter]));
    const ordered: CageHealthParameter[] = [];
    const selectedKeys = new Set<HealthParameterKey>();

    for (const parameter of parameters
        .filter(isCageHealthAlertParameter)
        .sort(compareByAlertPriority)) {
        ordered.push(parameter);
        selectedKeys.add(parameter.key);
    }

    for (const key of CAGE_DASHBOARD_HEALTH_PARAMETER_FILL_ORDER) {
        if (selectedKeys.has(key)) {
            continue;
        }
        const parameter = byKey.get(key);
        if (parameter == null) {
            continue;
        }
        ordered.push(parameter);
        selectedKeys.add(key);
    }

    return ordered;
}

export function selectCageHealthOverviewParameters(
    parameters: CageHealthParameter[]
): CageHealthParameter[] {
    return orderCageHealthOverviewParameters(parameters).slice(
        0,
        CAGE_DASHBOARD_HEALTH_PARAMETER_LIMIT
    );
}
